import { useEffect, useState } from "react";
import { Form, Col, Row, Modal } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  CheckCircleOutlined,
  CloseOutlined,
  UnorderedListOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import AccountCreationModal from "../../../global/components/AccountCreationModal";
import { FaFileInvoiceDollar } from "react-icons/fa6";
import DealerIcon from "../../../assets/images/vectors/SideBarIcons/DealerIcon";
import OemIcon from "../../../assets/images/vectors/SideBarIcons/OemIcon";
import PaymentIcon from "../../../assets/images/vectors/SideBarIcons/PaymentIcon";
import dayjs from "dayjs";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import { FaDollarSign } from "react-icons/fa";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { PurchaseItemsColumns } from "./PurchaseItemsColumns";
import MyPaymentForm from "../../../global/components/PaymentForm";
import {
  getPurchaseListPath,
  getPurchaseOpenPath,
  getPurchaseRowId,
} from "../utils/listSearchStorage";
import { getDealerNo, getOemNo, hasRole, getUserData } from "../../../global/hooks/getUserRole";
import { isPurchasePaid, purchaseTypeOptions } from "../../../utils/Common";
const PRE_PURCHASE_TYPE = 1;
const ON_SITE_PURCHASE_TYPE = 0;
export const PurchaseForm = ({ type, onCancel }) => {
  const userData = getUserData();
  /** Dealer login: no OEM/Dealer pickers; scope from session (see Device filter customer path). */
  const hideOemDealerForDealerAccount = hasRole(7, 8);
  /** OEM login: OEM is implied by the account, so only the Dealer field is shown. */
  const hideOemForOemAccount = hasRole(4, 5, 6);
  const role_value = userData?.role_value;
  const isSystemUser = hasRole(1, 2, 3);
  const { id } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [showCardLayout, setShowCardLayout] = useState(!isSystemUser);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [defaultData, setDefaultData] = useState({
    OemNo: null,
    DealerNo: null,
  });
  const [oemNo, setOemNo] = useState(null);
  const [totalAmount, setTotalAmount] = useState(null);
  const purchaseItemurl =
    id && `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/`;
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const url = id && `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`;
  const postUrl = `${process.env.REACT_APP_API_URL}/api/purchases/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`;
  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;
  const { data, loading, error, refetch } = useGet(url);

  useEffect(() => {
    if (!paymentModal) setShowCardLayout(!isSystemUser);
  }, [paymentModal, isSystemUser]);

  const hasApiDiscounted =
    data?.data?.Total != null && data?.data?.Total !== ""
      ? true
      : data?.data?.Amount_with_discount != null &&
        data?.data?.Amount_with_discount !== "";
  const discountedFromApi = hasApiDiscounted
    ? Number(
        data?.data?.Total != null && data?.data?.Total !== ""
          ? data.data.Total
          : data.data.Amount_with_discount,
      )
    : NaN;
  const baseFromApi = Number(data?.data?.SubTotal ?? data?.data?.Amount);
  const purchaseAmountNum = Number.isFinite(discountedFromApi)
    ? discountedFromApi
    : Number.isFinite(baseFromApi)
      ? baseFromApi
      : Number(totalAmount);
  const purchaseAmountProp =
    Number.isFinite(purchaseAmountNum) && purchaseAmountNum >= 0
      ? `$${purchaseAmountNum.toFixed(2)}`
      : "$0.00";
  const hasPaymentTime =
    data?.data?.PaymentTime != null && data?.data?.PaymentTime !== "";
  const isPaid = isPurchasePaid(data?.data);
  const isOnSitePurchase =
    Number(data?.data?.PurchaseType) === ON_SITE_PURCHASE_TYPE;
  const canManagePurchase = hasRole(1, 2, 7, 8);

  const {
    data: purchaseData,
    loading: purchaseLoading,
    error: purchaseError,
    refetch: purchaseRefetch,
  } = useGet(purchaseItemurl);

  const {
    data: optionData,
    loading: optionDataLoading,
    error: optionDataError,
    refetch: optionDataRefetch,
  } = useGet(optionUrl);
  const resolveOemNo = (value) => {
    if (value == null || value === "") return null;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
    const oemOption = optionData?.data?.oems?.find(
      (oem) => String(oem?.name) === String(value),
    );
    return oemOption?.value ?? null;
  };
  const resolvedOemNo =
    resolveOemNo(oemNo) ??
    resolveOemNo(defaultData?.OemNo) ??
    resolveOemNo(data?.data?.OemNo) ??
    (hideOemDealerForDealerAccount || hideOemForOemAccount
      ? resolveOemNo(getOemNo())
      : null);
  const dealerOptionsUrl =
    resolvedOemNo != null
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${resolvedOemNo}/dealers/`
      : null;
  const {
    data: dealerOptionsData,
    loading: dealerOptionsLoading,
    refetch: dealerOptionsRefetch,
  } = useGet(dealerOptionsUrl);
  const modifiedOems = getModifiedResponse(optionData, "oems");
  const modifiedDealers = getModifiedResponse(dealerOptionsData, null);

  const resolveDealerBillingType = (dealerNoValue) => {
    if (dealerNoValue === null || dealerNoValue === undefined) return null;
    if (dealerNoValue === "") return null;

    const dealerId = Number(dealerNoValue);
    if (!Number.isFinite(dealerId)) return null;

    const rawDealers = dealerOptionsData?.data;
    if (!Array.isArray(rawDealers)) return null;

    const dealer = rawDealers.find(
      (d) =>
        Number(d?.value) === dealerId ||
        Number(d?.DealerNo) === dealerId ||
        Number(d?.dealer_no) === dealerId,
    );

    const bt =
      dealer?.BillingType ??
      dealer?.billing_type ??
      dealer?.billingType ??
      dealer?.Billing_type ??
      null;

    if (bt === null || bt === undefined) return null;
    const btNum = Number(bt);
    return Number.isFinite(btNum) ? btNum : null;
  };
  const effectiveDealerNoForPayment =
    data?.data?.DealerNo ??
    form.getFieldValue("DealerNo") ??
    defaultData?.DealerNo ??
    (hideOemDealerForDealerAccount ? getDealerNo() : null);

  const dealerBillingTypeForPayment = resolveDealerBillingType(
    effectiveDealerNoForPayment,
  );

  const { error: postError, postData: postAction } = usePost(postUrl);
  const { error: putError, putData: putAction } = usePut(putUrl);
  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteData,
  } = useDelete(deleteUrl);
  const onFinish = async (values) => {
    const modValues = {
      PurchaseTime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      PurchaseType: PRE_PURCHASE_TYPE,
      ...(hideOemDealerForDealerAccount
        ? {
            OemNo: getOemNo(),
            DealerNo: getDealerNo(),
          }
        : {
            OemNo: values?.OemNo,
            DealerNo: values?.DealerNo,
          }),
    };
    try {
      const result = await postAction(modValues);
      if (result != null) {
        openConfirmNotification("Success");
        setTimeout(() => {
          const rid = getPurchaseRowId(result?.data ?? result);
          navigate(
            rid != null
              ? `/purchase/prepurchase/${rid}`
              : getPurchaseListPath(),
          );
        }, 200);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  const onReset = () => {
    onCancel ? onCancel() : navigate(-1);
  };

  const getPurchasePaidPayload = (paymentResponse) => {
    const values = form.getFieldsValue(true);
    const amount = Number(
      String(values?.Total ?? values?.Amount ?? "").replace(/[^0-9.-]+/g, ""),
    );
    const paymentNo =
      paymentResponse?.data?.PaymentNo ?? paymentResponse?.PaymentNo ?? null;
    const purchaseTime = values?.PurchaseTime
      ? dayjs(values.PurchaseTime).format("YYYY-MM-DD HH:mm:ss")
      : (data?.data?.PurchaseTime ?? null);

    return {
      PurchaseTime: purchaseTime,
      PurchaseType:
        values?.PurchaseType ?? data?.data?.PurchaseType ?? PRE_PURCHASE_TYPE,
      OemNo: hideOemDealerForDealerAccount ? getOemNo() : values?.OemNo,
      DealerNo: hideOemDealerForDealerAccount
        ? getDealerNo()
        : values?.DealerNo,
      Amount: Number.isFinite(amount) ? amount : 0,
      PaymentNo: paymentNo,
      // Keep the stored PO Number (system-user-only field) when paying from the info page.
      ...(String(values?.PoNumber ?? "").trim() || data?.data?.Comment
        ? {
            Comment:
              String(values?.PoNumber ?? "").trim() || data?.data?.Comment,
          }
        : {}),
    };
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };
  useEffect(() => {
    if (!id || loading) return;
    const getAccountInfoById = () => {
      try {
        form.resetFields();
        if (data?.data) {
          form.setFieldsValue({
            PurchaseTime: data.data.PurchaseTime
              ? dayjs(data.data.PurchaseTime)
              : null,
            PurchaseType:
              data.data.PurchaseType != null
                ? Number(data.data.PurchaseType)
                : PRE_PURCHASE_TYPE,
            OemNo: data?.data?.OemNo,
            OemName: data?.data?.OemNo ? String(data.data.OemNo) : "",
            DealerNo: data?.data?.DealerNo,
            SubTotal:
              data.data.SubTotal != null && data.data.SubTotal !== ""
                ? `$${Number(data.data.SubTotal).toFixed(2)}`
                : data.data.Amount != null && data.data.Amount !== ""
                  ? `$${Number(data.data.Amount).toFixed(2)}`
                  : "",
            Discount:
              data.data.Discount != null && data.data.Discount !== ""
                ? Number(data.data.PurchaseType) === ON_SITE_PURCHASE_TYPE
                  ? `${Number(data.data.Discount)}%`
                  : `$${Number(data.data.Discount).toFixed(2)}`
                : Number(data.data.PurchaseType) === ON_SITE_PURCHASE_TYPE
                  ? "0%"
                  : "$0.00",
            Total:
              data.data.Total != null && data.data.Total !== ""
                ? `$${Number(data.data.Total).toFixed(2)}`
                : data.data.Amount_with_discount != null &&
                    data.data.Amount_with_discount !== ""
                  ? `$${Number(data.data.Amount_with_discount).toFixed(2)}`
                  : data.data.Amount != null && data.data.Amount !== ""
                    ? `$${Number(data.data.Amount).toFixed(2)}`
                    : "",
            PaymentTime: data.data.PaymentTime
              ? dayjs(data.data.PaymentTime)
              : null,
            Special:
              data.data.Special != null && data.data.Special !== ""
                ? `$${Number(data.data.Special).toFixed(2)}`
                : "",
            PoNumber: data.data.Comment ?? "",
          });
          if (data?.data?.OemNo != null) {
            setOemNo(data.data.OemNo);
            setDefaultData((prev) => ({
              ...prev,
              OemNo: data.data.OemNo,
              DealerNo: data.data.DealerNo ?? null,
            }));
          }
        } else {
          console.warn("No data found for the given ID");
        }
      } catch (error) {
        if (!loading) {
          openErrorNotification("Error fetching data!");
        }
      }
    };

    getAccountInfoById();
  }, [id, data, loading, form, type]);

  useEffect(() => {
    const totalFromInfo = Number(
      data?.data?.Total ??
        data?.data?.Amount_with_discount ??
        data?.data?.Amount,
    );
    if (Number.isFinite(totalFromInfo)) {
      setTotalAmount(totalFromInfo);
      form.setFieldsValue({
        Total: `$${totalFromInfo.toFixed(2)}`,
      });
    }
  }, [data, form]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [postError]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [putError]);

  //toast for delete
  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate(getPurchaseListPath());
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    } else {
      return;
    }
  }, [deletedData, deletedDataError]);

  const breadcrumbItems = [
    {
      label: "Purchase",
      href: "/purchase",
    },
    {
      label: type === "info" ? "Information" : "Pre-Purchase",
      href: type === "info" ? `/purchase/info/${id}` : "/purchase/prepurchase",
    },
  ];
  const infoActionsReady =
    type === "info" &&
    Boolean(id) &&
    !loading &&
    Boolean(data?.data) &&
    (dealerOptionsUrl == null || !dealerOptionsLoading);
  const showPaymentOnInfo =
    infoActionsReady &&
    !isPaid &&
    !isOnSitePurchase &&
    Boolean(Number(data?.data?.Total));

  const buttonsData =
    type === "info"
      ? [
          ...(showPaymentOnInfo
            ? [
                {
                  label: "Payment",
                  icon: <FaDollarSign />,
                  onClick: () => setPaymentModal(true),
                },
              ]
            : []),
          ...(canManagePurchase && infoActionsReady && !isPaid
            ? [
                {
                  label: "Delete",
                  icon: <DeleteOutlined />,
                  onClick: () => {
                    setShowModal(true);
                  },
                },
              ]
            : []),
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => {
              navigate(getPurchaseListPath());
            },
          },
        ].filter(Boolean)
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
            disabled: isPaid,
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  useEffect(() => {
    if (type !== "info" || !id || loading || !data?.data) return;
    const openPath = getPurchaseOpenPath(data.data, canManagePurchase);
    if (openPath !== `/purchase/info/${id}`) {
      navigate(openPath, { replace: true });
    }
  }, [type, id, loading, data, navigate, canManagePurchase]);

  const handleCreateNew = (type) => {
    setModalType(type);
    setIsOpenModal(true);
  };
  useEffect(() => {
    optionDataRefetch();
    dealerOptionsRefetch();
    if (defaultData?.DealerNo) {
      form.setFieldsValue(defaultData);
    }
  }, [defaultData]);

  // Add form: default OEM to first option once options load
  useEffect(() => {
    if (id || type === "info" || hideOemDealerForDealerAccount) return;
    if (!modifiedOems?.length) return;

    const currentOem = form.getFieldValue("OemNo");
    if (currentOem != null && currentOem !== "") return;

    const firstOem = modifiedOems[0]?.value;
    if (firstOem == null) return;

    form.setFieldsValue({
      PurchaseTime: form.getFieldValue("PurchaseTime") || dayjs(),
      PurchaseType: PRE_PURCHASE_TYPE,
      OemNo: firstOem,
      DealerNo: null,
    });
    setOemNo(firstOem);
    setDefaultData((prev) => ({
      ...prev,
      OemNo: firstOem,
      DealerNo: null,
    }));
  }, [id, type, hideOemDealerForDealerAccount, modifiedOems, form]);

  return (
    <>
      <AccountCreationModal
        isOpenModal={isOpenModal}
        modalType={modalType}
        setIsOpenModal={setIsOpenModal}
        setDefaultData={setDefaultData}
        defaultData={defaultData}
        setModalType={setModalType}
        isModal={true}
        isModalForm={true}
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            {modalType === "Dealer" ? (
              <DealerIcon className="text-[15px] text-black ml-1" />
            ) : modalType === "OEM" ? (
              <OemIcon className="text-[15px] text-black ml-1" />
            ) : null}
            <span className="text-[14px] font-[Poppins] text-black  ml-2">
              {isOpenModal ? `Add ${modalType}` : "Send Invitation"}
            </span>
          </div>
        }
      />
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
        initialValues={{
          PurchaseType: PRE_PURCHASE_TYPE,
          PurchaseTime: dayjs(),
        }}
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>

          <div className={`bg-white overflow-hidden border rounded-[4px]`}>
            <div className="flex items-center bg-gray-200 p-2.5">
              <FaFileInvoiceDollar className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2 ">
                {type === "info" ? "Purchase" : "Pre-Purchase"}
              </span>
            </div>

            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                {type === "info" ? (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomDateTimePicker
                      name="PurchaseTime"
                      label="Purchase Time"
                      placeholder="Purchase Time"
                      format="YYYY-MM-DD HH:mm:ss"
                      disabled
                      loading={loading}
                    />
                  </Col>
                ) : null}
                {type === "info" ? (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomSelect
                      label="Purchase Type"
                      name="PurchaseType"
                      options={purchaseTypeOptions}
                      placeholder="Please select"
                      disabled
                      allowClear={false}
                      loading={loading}
                    />
                  </Col>
                ) : null}

                {!hideOemDealerForDealerAccount ? (
                  <>
                    {hideOemForOemAccount ? null : type === "info" ? (
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="OEM"
                          name="OemName"
                          disabled
                          loading={loading}
                        />
                      </Col>
                    ) : (
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomSelect
                          label="OEM"
                          name="OemNo"
                          options={modifiedOems}
                          disabled={type === "info"}
                          placeholder="Please select"
                          onChange={(value) => {
                            setDefaultData((prev) => ({
                              ...prev,
                              OemNo: value,
                              DealerNo: null,
                            }));
                            setOemNo(value);
                            form.setFieldsValue({
                              DealerNo: null,
                            });
                          }}
                          required
                          handleCreateNew={handleCreateNew}
                          loading={optionDataLoading}
                          type={"OEM"}
                        />
                      </Col>
                    )}
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <CustomSelect
                        label="Dealer"
                        name="DealerNo"
                        options={modifiedDealers}
                        disabled={
                          type === "info" || !form.getFieldValue("OemNo")
                        }
                        placeholder="Please select"
                        onChange={(value) => {
                          setDefaultData((prev) => ({
                            ...prev,
                            DealerNo: value,
                          }));
                        }}
                        required
                        handleCreateNew={handleCreateNew}
                        loading={dealerOptionsLoading}
                        type={"Dealer"}
                      />
                    </Col>
                  </>
                ) : null}
                {type === "info" && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="SubTotal"
                      name="SubTotal"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Discount"
                      name="Discount"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && isSystemUser && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Special Discount"
                      name="Special"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Total"
                      name="Total"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && hasPaymentTime && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomDateTimePicker
                      name="PaymentTime"
                      label="Payment Time"
                      placeholder="Payment Time"
                      format="YYYY-MM-DD HH:mm:ss"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && isSystemUser && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="PO Number"
                      name="PoNumber"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
              </Row>
            </div>
          </div>
        </div>
      </Form>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
      <Modal
        title={
          <div className="flex items-center justify-between bg-gray-200 p-2.5 rounded-md">
            <div className="flex items-center">
              <PaymentIcon className="text-[18px] text-black " />
              <span className="text-[14px] font-[Poppins] text-black  ml-2">
                Make Payment
              </span>
            </div>
            {/* {![7, 8].includes(role_value) && (
              <Button
                type="primary"
                className="min-w-[140px] h-[30px] py-1 px-[10px] font-medium"
                onClick={() => setShowCardLayout((prev) => !prev)}
              >
                {showCardLayout ? "Manual Payment" : "Pay with Card"}
              </Button>
            )} */}
          </div>
        }
        open={paymentModal}
        onCancel={() => {
          setPaymentModal(false);
          setShowCardLayout(!isSystemUser);
        }}
        footer={null}
        closeIcon={false}
        width={560}
        centered
        destroyOnClose
      >
        <div>
          <MyPaymentForm
            DealerNo={
              data?.data?.DealerNo ??
              (hideOemDealerForDealerAccount ? getDealerNo() : undefined)
            }
            amount={purchaseAmountProp}
            setPaymentModal={setPaymentModal}
            InvoiceNo={id}
            OrderName={
              data?.data?.PurchaseTime
                ? String(data.data.PurchaseTime)
                : "Purchase"
            }
            refetch={refetch}
            showCardLayout={showCardLayout}
            setShowCardLayout={setShowCardLayout}
            onPaymentSuccess={async (paymentResponse) => {
              const result = await putAction(
                getPurchasePaidPayload(paymentResponse),
              );
              if (result != null) {
                refetch();
                purchaseRefetch();
              }
            }}
          />
        </div>
      </Modal>
      {type === "info" && (
        <>
          <div className="mt-2"></div>
          <GlobalTable
            columns={PurchaseItemsColumns({
              showUsedColumns: isPaid,
            })}
            dataSource={
              purchaseData?.data
                ?.map((item) => ({
                  ...item,
                  key: item.ItemNo,
                }))
                ?.sort((a, b) => b.ItemNo - a.ItemNo) || []
            }
            loading={purchaseLoading}
          />
        </>
      )}
    </>
  );
};
